import FJCanvasPool from '../FJCanvasPool';
import FJDomUtil from '../../util/FJDomUtil';
import FJCanvasUtil from '../../util/FJCanvasUtil';
import FJPoolStore from '../../store/FJPoolStore';
import { isObject } from 'lodash-es';
import FJCssPerspectiveToWebGLManager from '../../store/FJCssPerspectiveToWebGL/FJCssPerspectiveToWebGLManager';
class FJDomCanvasRender {
    _containerInfoCache = new Map();

    /**
     * @desc 渲染dom视图中的canvas元素
     * @param view
     * @param inGroup, 是否在group中
     * @return {boolean|HTMLCanvasElement}
     */
    render(view, inGroup = false) {
        // 创建画布
        let size = { width: view.offsetWidth, height: view.offsetHeight };
        if (size.width === 0 || size.height === 0) {
            size = FJDomUtil.getDomSize(view);
        }
        let canvas = FJCanvasPool.getCanvas();
        if (canvas.width !== size.width || canvas.height !== size.height) {
            canvas.width = size.width;
            canvas.height = size.height;
        }
        const ret = this.renderDomToCanvas(view, canvas, inGroup);
        if (ret) {
            return canvas;
        }

        FJCanvasPool.releaseCanvas(canvas);
        return false;
    }

    /**
     * @desc 渲染dom视图中的canvas元素
     * @param dom
     * @param inGroup, 是否在group中
     * @param domExpandSize, dom中的canvas在渲染时的扩展大小
     * @return {boolean|HTMLCanvasElement}
     */
    renderDomToCanvas(dom, canvas, inGroup = false, domExpandSize = 0, options = {}) {
        if (!dom) {
            return false;
        }
        try {
            // 创建画布
            let ctx = canvas.getContext('2d');

            // 渲染
            if (inGroup) {
                return this._renderDomInGroup(dom, ctx, domExpandSize, options);
            }

            return this._renderDom(dom, ctx, domExpandSize, options);
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    /**
     * @desc 渲染dom视图到context中
     * @param view
     * @param ctx
     * @param domExpandSize {number | {horizontal: number, vertical: number}}
     * @param options
     * @param options.getClipRenderRect {function} 是否是导出时获取裁剪区域
     * @param options.isExpandMotionContainer {boolean} 是否是扩充动画容器
     * @return {boolean}
     * @private
     */
    _renderDom(view, ctx, domExpandSize, options = {}) {
        const { shadowData, isExpandMotionContainer } = options;
        const eleContainer = view.getElementsByTagName('div')[0];
        if (!eleContainer) return false;
        let children = Array.from(eleContainer.getElementsByTagName('canvas')).filter(
            c => c.style.display !== 'none' && c.width !== 0 && c.height !== 0,
        );

        let eleCanvas = null;
        let motionTarget = null;
        const hasShadow = !!shadowData;
        let shadowCanvas = hasShadow && shadowData.canvas;
        // 是否提前处理透明度
        const preHandleOpacity = !hasShadow;

        let tempCanvas = null;
        let tempCtx = null;
        let currentCtx = ctx;

        if (!preHandleOpacity) {
            tempCanvas = FJPoolStore.getCanvas();
            tempCanvas.width = ctx.canvas.width;
            tempCanvas.height = ctx.canvas.height;
            tempCtx = tempCanvas.getContext('2d');

            currentCtx = tempCtx;
        }

        if (isExpandMotionContainer) {
            motionTarget = eleContainer.children[0];
            if (children.length === 1) {
                eleCanvas = children[0];
            } else if (children.length === 2 && hasShadow) {
                eleCanvas = children[1];
            } else {
                return false;
            }
        } else if (children.length == 1) {
            eleCanvas = children[0];
            motionTarget = eleCanvas;
        } else {
            return false;
        }

        // 运动追踪数据处理
        let translateX = 0;
        let translateY = 0;
        try {
            const eleContent = eleContainer.getElementsByTagName('div')[0];
            const translate = eleContent
                ? eleContent.style.transform.replace('translate(', '')
                : eleCanvas.style.transform.replace('translate(', '');
            const translates = translate.split(',');
            translateX = parseFloat(translates[0]) || 0;
            translateY = translates.length > 1 ? parseFloat(translates[1]) : translateX;
        } catch (e) {
            translateX = 0;
            translateY = 0;
        }

        // 是否存在混合模式
        const blendMode = eleContainer.style.mixBlendMode;
        const existBlend = blendMode && blendMode !== 'normal';

        // 开始渲染
        currentCtx.save();
        // 如果不提前处理透明度会在后面统一做一次渲染，所以这里需要判断是否提前来决定是否设置混合模式
        if (existBlend && preHandleOpacity) {
            currentCtx.globalCompositeOperation = blendMode;
        }

        // 应用元素的容器层transform
        const eleData = this._applyContainerToContext(eleContainer, currentCtx, { px: 0, py: 0, preHandleOpacity });

        // 应用元素的motion动画层transform
        const eleMotionData = this._applyMotionToContext(
            motionTarget,
            currentCtx,
            {
                ...eleData,
                preHandleOpacity,
            },
            eleCanvas,
            isExpandMotionContainer,
        );

        if (eleMotionData.is3DMotion) {
            const perspective = eleMotionData.Motion3DData.perspective;
            const transforms = eleMotionData.Motion3DData.transforms;
            if (perspective) {
                FJCssPerspectiveToWebGLManager.convertCssPerspectiveToWebGL({
                    canvas: eleCanvas,
                    transforms,
                    perspective,
                    isRebindTexture: options.isRebindTexture,
                    id: options.id,
                    usePublic: options.usePublic,
                });
                hasShadow &&
                    FJCssPerspectiveToWebGLManager.convertCssPerspectiveToWebGL({
                        canvas: shadowCanvas,
                        transforms,
                        perspective,
                        isRebindTexture: options.isRebindTexture,
                        id: options.id + '_shadow',
                        usePublic: true,
                    });
            }
        }
        // 应用元素的阴影
        // const shadowData = shadowCanvas ? this._applyShadowToContext(shadowCanvas) : null;

        const domExpandHorizontal = isObject(domExpandSize) ? domExpandSize.horizontal : domExpandSize;
        const domExpandVertical = isObject(domExpandSize) ? domExpandSize.vertical : domExpandSize;

        let finalCanvas = eleCanvas;
        if (eleMotionData.clipPath) {
            const clipRenderRect = options.getClipRenderRect
                ? options.getClipRenderRect()
                : {
                      expandWidth: domExpandHorizontal,
                      expandHeight: domExpandVertical,
                  };
            finalCanvas = this.clipCanvas(eleCanvas, eleMotionData.clipPath, clipRenderRect);
        }

        const width = finalCanvas.width;
        const height = finalCanvas.height;
        currentCtx.translate(-domExpandHorizontal, -domExpandVertical);
        if (hasShadow && (!eleMotionData.clipPath || eleMotionData.clipPath === 'initial')) {
            const { expandSize, offset } = shadowData;
            const offsetX = -expandSize.horizontal + offset.x;
            const offsetY = -expandSize.vertical + offset.y;
            currentCtx.drawImage(
                shadowCanvas,
                eleMotionData.px + translateX + offsetX + domExpandHorizontal - eleMotionData.targetOffset.x,
                eleMotionData.py + translateY + offsetY + domExpandVertical - eleMotionData.targetOffset.y,
                shadowCanvas.width,
                shadowCanvas.height,
            );
        }
        currentCtx.drawImage(
            finalCanvas,
            0,
            0,
            width,
            height,
            eleMotionData.px + translateX,
            eleMotionData.py + translateY,
            width,
            height,
        );
        currentCtx.restore();

        if (eleMotionData.clipPath) {
            FJPoolStore.releaseCanvas(finalCanvas);
        }

        if (!preHandleOpacity) {
            const combinedStyle = {
                opacity: eleMotionData.opacity * eleData.opacity,
                blendMode: existBlend ? eleContainer.style.mixBlendMode : undefined,
            };
            this._mergeCombinedStyle(combinedStyle, ctx, tempCtx);
            FJPoolStore.releaseCanvas(tempCanvas);
        }

        return true;
    }

    /**
     * @desc 处理画面组合的样式，返回合成后的画面
     */
    _mergeCombinedStyle(styleObject, ctx, tempCtx) {
        const tempCanvas = tempCtx.canvas;
        ctx.save();
        ctx.globalAlpha = styleObject.opacity;
        if (styleObject.blendMode) {
            ctx.globalCompositeOperation = styleObject.blendMode;
        }
        ctx.drawImage(tempCanvas, 0, 0);
        ctx.restore();
    }

    clipCanvas(eleCanvas, clipPath, options = {}) {
        const clipToCanvas = FJPoolStore.getCanvas();
        const clipCtx = clipToCanvas.getContext('2d');
        const { expandWidth = 0, expandHeight = 0, rect } = options;
        clipToCanvas.width = eleCanvas.width;
        clipToCanvas.height = eleCanvas.height;
        clipCtx.save();
        const clipRect = rect || {
            x: expandWidth,
            y: expandHeight,
            width: eleCanvas.width - 2 * expandWidth,
            height: eleCanvas.height - 2 * expandHeight,
        };
        FJCanvasUtil.clipToCanvas(clipCtx, clipPath, clipRect);
        clipCtx.drawImage(eleCanvas, 0, 0);
        clipCtx.restore();
        return clipToCanvas;
    }

    /**
     * @desc 渲染group dom视图到context中
     * @param view
     * @param ctx
     * @param domExpandSize {number | {horizontal: number, vertical: number}}
     * @return {boolean}
     * @private
     */
    _renderDomInGroup(view, ctx, domExpandSize, options = {}) {
        const { shadowData, isExpandMotionContainer = false } = options;
        const groupContainer = view.getElementsByTagName('div')[0];
        if (!groupContainer) return false;
        const groupMotion = groupContainer.getElementsByTagName('div')[0];
        if (!groupMotion) return false;
        const eleContainer = groupMotion.getElementsByTagName('div')[0];
        if (!eleContainer) return false;
        let eleMotion = null;
        const children = Array.from(eleContainer.getElementsByTagName('canvas')).filter(
            c => c.style.display !== 'none' && c.width !== 0 && c.height !== 0,
        );
        const hasShadow = !!shadowData;
        let eleCanvas = null;
        let shadowCanvas = hasShadow && shadowData.canvas;
        // 是否提前处理透明度
        const preHandleOpacity = !hasShadow;
        let currentCtx = ctx;
        let tempCanvas = null;
        let tempCtx = null;

        if (!preHandleOpacity) {
            tempCanvas = FJPoolStore.getCanvas();
            tempCanvas.width = ctx.canvas.width;
            tempCanvas.height = ctx.canvas.height;
            tempCtx = tempCanvas.getContext('2d');

            currentCtx = tempCtx;
        }

        if (isExpandMotionContainer) {
            eleMotion = eleContainer.getElementsByTagName('div')[0];
            if (children.length === 1) {
                eleCanvas = children[0];
            } else if (children.length === 2) {
                eleCanvas = children[1];
            } else {
                return false;
            }
        } else if (children.length === 1) {
            eleCanvas = children[0];
            eleMotion = eleCanvas;
        } else {
            return false;
        }
        if (!eleMotion) return false;

        // 运动追踪数据处理
        let translateX = 0;
        let translateY = 0;
        try {
            translateX = parseFloat(groupMotion.getAttribute('data-translate-x')) || 0;
            translateY = parseFloat(groupMotion.getAttribute('data-translate-y')) || 0;
        } catch (e) {
            console.log(e);
        }

        // 是否存在混合模式
        const blendMode = eleContainer.style.mixBlendMode;
        const existBlend = blendMode && blendMode !== 'normal';
        // 开始渲染
        currentCtx.save();
        if (existBlend && preHandleOpacity) {
            currentCtx.globalCompositeOperation = blendMode;
        }
        // 应用group的容器层的transform
        const groupData = this._applyContainerToContext(groupContainer, currentCtx, { px: 0, py: 0, preHandleOpacity });
        // 应用group的motion动画层transform
        // groupMotion dom
        const groupMotionData = this._applyMotionToContext(groupMotion, currentCtx, {
            ...groupData,
            px: groupData.px + translateX,
            py: groupData.py + translateY,
            preHandleOpacity,
        });

        // 应用元素的容器层transform
        const eleData = this._applyContainerToContext(eleContainer, currentCtx, {
            ...groupMotionData,
            preHandleOpacity,
        });
        // 应用元素的motion动画层transform
        const eleMotionData = this._applyMotionToContext(
            eleMotion,
            currentCtx,
            {
                ...eleData,
                preHandleOpacity,
            },
            eleCanvas,
            isExpandMotionContainer,
        );

        if (eleMotionData.is3DMotion) {
            const perspective = eleMotionData.Motion3DData.perspective;
            const transforms = eleMotionData.Motion3DData.transforms;
            if (perspective) {
                FJCssPerspectiveToWebGLManager.convertCssPerspectiveToWebGL({
                    canvas: eleCanvas,
                    transforms,
                    perspective,
                    isRebindTexture: options.isRebindTexture,
                    id: options.id,
                    usePublic: options.usePublic,
                });
            }
            hasShadow &&
                FJCssPerspectiveToWebGLManager.convertCssPerspectiveToWebGL({
                    canvas: shadowCanvas,
                    transforms,
                    perspective,
                    isRebindTexture: options.isRebindTexture,
                    id: options.id + '_shadow',
                    usePublic: options.usePublic,
                });
        }

        // const shadowData = shadowCanvas ? this._applyShadowToContext(shadowCanvas) : null;
        const dstSize = {
            width: eleCanvas.style.width ? parseFloat(eleCanvas.style.width) : eleCanvas.width,
            height: eleCanvas.style.height ? parseFloat(eleCanvas.style.height) : eleCanvas.height,
        };
        const domExpandHorizontal = isObject(domExpandSize) ? domExpandSize.horizontal : domExpandSize;
        const domExpandVertical = isObject(domExpandSize) ? domExpandSize.vertical : domExpandSize;

        let finalCanvas = eleCanvas;
        if (eleMotionData.clipPath && eleMotionData.clipPath !== 'initial') {
            const clipRenderRect = options.getClipRenderRect
                ? options.getClipRenderRect()
                : {
                      expandWidth: domExpandHorizontal,
                      expandHeight: domExpandVertical,
                  };
            finalCanvas = this.clipCanvas(eleCanvas, eleMotionData.clipPath, clipRenderRect);
        }

        const width = finalCanvas.width;
        const height = finalCanvas.height;
        currentCtx.translate(-domExpandHorizontal, -domExpandVertical);

        if (hasShadow && (!eleMotionData.clipPath || eleMotionData.clipPath === 'initial')) {
            const { expandSize, offset } = shadowData;
            const offsetX = -expandSize.horizontal + offset.x;
            const offsetY = -expandSize.vertical + offset.y;

            currentCtx.drawImage(
                shadowCanvas,
                eleMotionData.px + offsetX + domExpandHorizontal - eleMotionData.targetOffset.x,
                eleMotionData.py + offsetY + domExpandVertical - eleMotionData.targetOffset.y,
                shadowCanvas.width,
                shadowCanvas.height,
            );
        }
        currentCtx.drawImage(
            finalCanvas,
            0,
            0,
            width,
            height,
            eleMotionData.px,
            eleMotionData.py,
            dstSize.width + 2 * domExpandHorizontal,
            dstSize.height + 2 * domExpandVertical,
        );
        currentCtx.restore();

        if (eleMotionData.clipPath && eleMotionData.clipPath !== 'initial') {
            FJPoolStore.releaseCanvas(finalCanvas);
        }

        if (!preHandleOpacity) {
            const combinedStyle = {
                opacity: groupData.opacity * groupMotionData.opacity * eleData.opacity * eleMotionData.opacity,
                blendMode: existBlend ? eleContainer.style.mixBlendMode : undefined,
            };
            this._mergeCombinedStyle(combinedStyle, ctx, tempCtx);
            FJPoolStore.releaseCanvas(tempCanvas);
        }

        return true;
    }

    _getContainerSize(container) {
        const style = container.style.cssText;
        if (this._containerInfoCache.has(style)) {
            return this._containerInfoCache.get(style);
        }

        let width = container.offsetWidth;
        let height = container.offsetHeight;
        if (!width || !height) {
            const computedStyle = window.getComputedStyle(container);
            width = parseFloat(computedStyle.width);
            height = parseFloat(computedStyle.height);
        }

        this._containerInfoCache.set(style, { width, height });
        return { width, height };
    }

    /**
     * @desc 应用容器层的transform到context中
     * @param container
     * @param ctx
     * @return {{px: number, py: number}}
     * @private
     */
    _applyContainerToContext(container, ctx, data) {
        const { preHandleOpacity } = data;
        const transforms = container.style.transform.split(') ');
        const { width, height } = this._getContainerSize(container);
        const opacity = container.style.opacity ? parseFloat(container.style.opacity) : 1;
        let px = data.px + parseFloat(container.style.left);
        let py = data.py + parseFloat(container.style.top);

        // 透明度
        preHandleOpacity && (ctx.globalAlpha *= opacity);

        // 应用group container层的transform，这一层只有rotate和scale(水平和垂直翻转)
        transforms.forEach(transform => {
            if (transform.indexOf('translate(') !== -1) {
                const translate = transform.replace('translate(', '');
                const translates = translate.split(',');
                const translateX = parseFloat(translates[0]);
                const translateY = translates.length > 1 ? parseFloat(translates[1]) : translateX;

                if (translates[0].indexOf('%') !== false) {
                    // 百分比
                    ctx.translate((width * translateX) / 100, (height * translateY) / 100);
                } else {
                    ctx.translate(translateX, translateY);
                }
            } else if (transform.indexOf('translateX') !== -1) {
                const translateX = parseFloat(transform.replace('translateX(', ''));
                const isPercentage = transform.indexOf('%') !== false;
                if (isPercentage) {
                    ctx.translate((width * translateX) / 100, 0);
                } else {
                    ctx.translate(translateX, 0);
                }
            } else if (transform.indexOf('translateY') !== -1) {
                const translateY = parseFloat(transform.replace('translateY(', ''));
                const isPercentage = transform.indexOf('%') !== false;
                if (isPercentage) {
                    ctx.translate(0, (height * translateY) / 100);
                } else {
                    ctx.translate(0, translateY);
                }
            } else if (transform.indexOf('rotate') !== -1) {
                const degree = parseFloat(transform.replace('rotate(', ''));
                const angleInRadians = (degree * Math.PI) / 180;

                const ox = px + width / 2;
                const oy = py + height / 2;

                ctx.translate(ox, oy);
                ctx.rotate(angleInRadians);
                ctx.translate(-ox, -oy);
            } else if (transform.indexOf('scale') !== -1) {
                transform = transform.replace('scale(', '');
                const scales = transform.split(',');
                const scaleX = parseFloat(scales[0]);
                const scaleY = scales.length > 1 ? parseFloat(scales[1]) : scaleX;

                const ox = px + width / 2;
                const oy = py + height / 2;

                ctx.translate(ox, oy);
                ctx.scale(scaleX, scaleY);
                ctx.translate(-ox, -oy);
            }
        });

        // 应用裁剪
        const needCrop = container.style.overflow === 'hidden';
        if (needCrop) {
            ctx.beginPath();
            ctx.rect(px, py, width, height);
            ctx.clip();
        }

        return { px, py, opacity };
    }

    /**
     * @desc 应用动画层的transform到context中
     * @param motionTarget
     * @param ctx
     * @param data
     * @return {{px: *, py: *, opacity: number}}
     * @private
     */
    _applyMotionToContext(motionTarget, ctx, data, motionCanvas, isExpandMotionContainer) {
        const { px, py, preHandleOpacity } = data;
        const opacity = motionTarget.style.opacity ? parseFloat(motionTarget.style.opacity) : 1;
        const origin = motionTarget.style.transformOrigin;
        const transforms = motionTarget.style.transform.split(' ');
        const motionItem = isExpandMotionContainer ? motionCanvas : motionTarget;
        const isCanvas = motionItem instanceof HTMLCanvasElement;
        const width = motionItem.style.width
            ? parseFloat(motionItem.style.width)
            : isCanvas
              ? motionItem.width
              : this._getContainerSize(motionItem).width;
        const height = motionItem.style.height
            ? parseFloat(motionItem.style.height)
            : isCanvas
              ? motionItem.height
              : this._getContainerSize(motionItem).height;
        const targetOffset = {
            x: motionItem?.style.left ? parseFloat(motionItem.style.left) : 0,
            y: motionItem?.style.top ? parseFloat(motionItem.style.top) : 0,
        };
        let x = px + targetOffset.x;
        let y = py + targetOffset.y;
        // 获取transformOrigin
        let origins = origin.split(' ');
        const originX = origins[0];
        const originY = origins.length > 1 ? origins[1] : origins[0];

        // 渲染
        preHandleOpacity && (ctx.globalAlpha *= opacity);

        let perspective = 0;

        const perspectiveTransform = transforms.find(transform => transform.indexOf('perspective') !== -1);
        if (perspectiveTransform) {
            perspective = parseFloat(perspectiveTransform.replace('perspective(', '').replace('px)', ''));
        }

        const getOrigin = () => {
            let ox, oy;
            switch (originX) {
                case 'left':
                    ox = x;
                    break;
                case 'center':
                    ox = x + width / 2;
                    break;
                case 'right':
                    ox = x + width;
                    break;
                default:
                    if (!originX) {
                        ox = x + width / 2;
                        break;
                    }
                    if (originX.indexOf('%') !== -1) {
                        ox = x + (width * parseFloat(originX)) / 100;
                    } else {
                        ox = x + parseFloat(originX);
                    }
                    break;
            }
            switch (originY) {
                case 'top':
                    oy = y;
                    break;
                case 'center':
                    oy = y + height / 2;
                    break;
                case 'bottom':
                    oy = y + height;
                    break;
                default:
                    if (!originY) {
                        oy = y + height / 2;
                        break;
                    }
                    if (originY.indexOf('%') !== -1) {
                        oy = y + (height * parseFloat(originY)) / 100;
                    } else {
                        oy = y + parseFloat(originY);
                    }
                    break;
            }
            return { ox, oy };
        };

        if (perspective === 0) {
            transforms.forEach(transform => {
                if (transform.indexOf('translateX') !== -1) {
                    const translateX = parseFloat(transform.replace('translateX(', ''));
                    const isPercentage = transform.indexOf('%') !== false;
                    if (isPercentage) x += (width * translateX) / 100;
                    else x += translateX;
                } else if (transform.indexOf('translateY') !== -1) {
                    const translateY = parseFloat(transform.replace('translateY(', ''));
                    const isPercentage = transform.indexOf('%') !== false;
                    if (isPercentage) y += (height * translateY) / 100;
                    else y += translateY;
                } else if (transform.indexOf('scale') !== -1) {
                    const scale = parseFloat(transform.replace('scale(', ''));

                    const { ox, oy } = getOrigin();
                    ctx.translate(ox, oy);
                    ctx.scale(scale, scale);
                    ctx.translate(-ox, -oy);
                } else if (transform.indexOf('rotateX') !== -1) {
                    const degree = parseFloat(transform.replace('rotateX(', ''));
                    const angleInRadians = (degree * Math.PI) / 180;

                    const { ox, oy } = getOrigin();
                    ctx.translate(ox, oy);
                    // 创建3D旋转矩阵
                    const cosAngle = Math.cos(angleInRadians);
                    const sinAngle = Math.sin(angleInRadians);
                    const rotateMatrix = [
                        [1, 0, 0],
                        [0, cosAngle, -sinAngle],
                        [0, sinAngle, cosAngle],
                    ];
                    // 将3D旋转矩阵应用于Canvas的transform
                    ctx.transform(rotateMatrix[0][0], rotateMatrix[1][0], rotateMatrix[0][1], rotateMatrix[1][1], 0, 0);
                    ctx.translate(-ox, -oy);
                } else if (transform.indexOf('rotateY') !== -1) {
                    const degree = parseFloat(transform.replace('rotateY(', ''));
                    const angleInRadians = (degree * Math.PI) / 180;

                    const { ox, oy } = getOrigin();
                    ctx.translate(ox, oy);
                    // 创建3D旋转矩阵
                    const cosAngle = Math.cos(angleInRadians);
                    const sinAngle = Math.sin(angleInRadians);
                    const rotateMatrix = [
                        [cosAngle, 0, sinAngle],
                        [0, 1, 0],
                        [-sinAngle, 0, cosAngle],
                    ];

                    // 将3D旋转矩阵应用于Canvas的transform
                    ctx.transform(rotateMatrix[0][0], rotateMatrix[1][0], rotateMatrix[0][1], rotateMatrix[1][1], 0, 0);
                    ctx.translate(-ox, -oy);
                } else if (transform.indexOf('rotate') !== -1) {
                    const degree = parseFloat(transform.replace('rotate(', ''));
                    const angleInRadians = (degree * Math.PI) / 180;
                    const { ox, oy } = getOrigin();

                    ctx.translate(ox, oy);
                    ctx.rotate(angleInRadians);
                    ctx.translate(-ox, -oy);
                }
            });
        }
        const clipPath = motionTarget.style['clip-path'];

        return {
            px: x,
            py: y,
            opacity,
            clipPath,
            is3DMotion: perspective !== 0,
            Motion3DData: { transforms, perspective },
            targetOffset,
        };
    }
    _applyShadowToContext(shadowCanvas) {
        if (!shadowCanvas) return {};
        const px = Number(shadowCanvas.style.left.replace('px', ''));
        const py = Number(shadowCanvas.style.top.replace('px', ''));
        const filter = shadowCanvas.style.filter;
        return { px, py, filter };
    }
}

let instance = new FJDomCanvasRender();
export default instance;
